<template>
  <SidePanel
    ref="sidePanel"
    headline="Playlist Medias"
    subtitle="Listen to previews and select songs"
    placement="right"
    class="!w-full sm:!w-96"
    showMinimize
    @onclose="onClose"
    @onminimize="onMinimize"
  >
    <div class="space-y-4">
      <!-- Hidden audio element for non-YouTube previews -->
      <audio 
        ref="audioPlayer" 
        class="hidden" 
        @ended="audioEnded"
        @timeupdate="onAudioTimeUpdate"
        @loadedmetadata="onAudioLoadedMetadata"
      ></audio>
      
      <!-- Play in sequence switch -->
      <div v-if="playlist && playlist.songs && playlist.songs.length > 0" class="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/80 mb-2">
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ t('playlist.playInSequence') }}</span>
        <label for="play-in-sequence-toggle" class="flex items-center cursor-pointer select-none">
          <div class="relative">
            <input type="checkbox" id="play-in-sequence-toggle" class="sr-only" v-model="playInSequence">
            <div class="block w-10 h-6 rounded-full transition-colors" :class="playInSequence ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'"></div>
            <div class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200" :class="{'transform translate-x-4': playInSequence}"></div>
          </div>
        </label>
      </div>

      <div v-if="!playlist" class="flex justify-center py-8">
        <Loading />
      </div>
      <div v-else-if="!playlist.songs || playlist.songs.length === 0" class="text-gray-500 dark:text-gray-400 text-center py-8">
        No songs in this playlist.
      </div>
      <ul v-else class="space-y-2">
        <li
          v-for="(item, index) in processedSongs"
          :key="item.id"
          class="flex p-3 rounded-lg border transition-all duration-200"
          :class="[
            item.id === currentSongId 
              ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800' 
              : 'bg-white border-gray-100 hover:border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-600',
            playingSongId === item.id ? 'flex-col items-stretch' : 'items-center justify-between'
          ]"
        >
          <!-- Left: Play/Stop/Blocked Icon -->
          <div class="flex items-center flex-1 min-w-0 mr-3" :class="{ 'mr-0': playingSongId === item.id }">
            <div class="mr-3 flex-shrink-0 w-8 h-8 flex items-center justify-center">
              <!-- If loading media info -->
              <span v-if="item.loading" class="animate-pulse inline-block w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700"></span>
              
              <!-- If no media -->
              <div v-else-if="!item.hasMedia" class="flex items-center text-red-500 dark:text-red-400" title="No Media">
                <NoSymbolIcon class="w-6 h-6" />
              </div>
              
              <!-- Play / Pause Icon -->
              <Tooltip v-else :text="isPlayingSong(item) ? 'Pause preview' : 'Play preview'" position="top">
                <button
                  type="button"
                  @click="togglePlay(item)"
                  class="relative size-8 flex items-center justify-center p-1 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full hover:bg-gradient-to-r hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 active:scale-95 touch-manipulation select-none"
                >
                  <PauseIcon v-if="isPlayingSong(item)" class="size-5 transition-all pointer-events-none" />
                  <PlayIcon v-else class="size-5 transition-all translate-x-[1px] pointer-events-none" />
                </button>
              </Tooltip>
            </div>

            <!-- Middle: Song Name & Artist / Click opens lyric -->
            <div class="flex-1 min-w-0 flex flex-col justify-center">
              <button
                @click="selectSong(item.originalSong)"
                class="text-left font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 truncate w-full block"
                :class="{'font-bold text-indigo-600 dark:text-indigo-400': item.id === currentSongId}"
                title="Click to view lyrics"
              >
                {{ index + 1 }}. {{ item.name }}
              </button>
              <p v-if="item.artist" class="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                {{ item.artist }}
              </p>
              <p v-if="hasYoutubeMedia(item)" class="text-[11px] font-medium text-indigo-500 dark:text-indigo-400 mt-0.5">
                &#9654; Vídeo
              </p>
            </div>
          </div>

          <!-- Expanded YouTube frame: full card width, always inside the card
               (pure 16:9 so the video fits with no side bars).
               Rendered only while the panel is visible; when hidden, the
               mini player hosts its own instance (handoff below). -->
          <div v-if="isExpandedYt(item) && panelVisible" class="mt-2 w-full" @click.stop>
            <div class="yt-frame relative w-full overflow-hidden rounded-lg bg-black" style="aspect-ratio: 16 / 9;">
                  <SafeYoutube
                    :key="youtubeVideoId"
                    :ref="setAccordionPlayerRef"
                :videoid="youtubeVideoId"
                :autoplay="isPlaying ? 1 : 0"
                :loop="0"
                :controls="0"
                @ended="youtubeEnded"
                @paused="youtubePaused"
                @played="youtubePlayed"
              />
            </div>
            <p class="mt-1 text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Vídeo</p>
          </div>

          <!-- Progress Bar & Seek Controls (full width when playing) -->
          <div v-if="playingSongId === item.id" class="mt-2 w-full flex flex-col space-y-1" @click.stop>
                <!-- Progress bar slider -->
                <div class="flex items-center space-x-2">
                  <span class="text-[10px] text-gray-500 dark:text-gray-400 w-8 text-right">{{ formatTime }}</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.1"
                    :value="currentTimePercentage"
                    @input="seekMedia"
                    class="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                  />
                  <span class="text-[10px] text-gray-500 dark:text-gray-400 w-8">{{ formatDuration }}</span>
                </div>
                
                <!-- Skip backward / forward buttons -->
                <div class="flex items-center justify-center space-x-4 pt-1">
                  <Tooltip text="Backward 10s" position="top">
                    <button
                      type="button"
                      @click.stop="skipTime(-10)"
                      class="p-1 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-white transition-colors touch-manipulation select-none active:scale-95"
                    >
                      <svg class="w-4 h-4 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.334 4z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8L4.066 11.2z" />
                      </svg>
                    </button>
                  </Tooltip>
                  
                  <Tooltip text="Forward 10s" position="top">
                    <button
                      type="button"
                      @click.stop="skipTime(10)"
                      class="p-1 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-white transition-colors touch-manipulation select-none active:scale-95"
                    >
                      <svg class="w-4 h-4 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.934 12.8a1 1 0 000-1.6l-5.334-4A1 1 0 005 8v8a1 1 0 001.6.8l5.334-4z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.934 12.8a1 1 0 000-1.6l-5.334-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.334-4z" />
                      </svg>
                    </button>
                  </Tooltip>
                </div>
              </div>

          <!-- Right: Open Player Icon -->
          <!-- <div v-if="item.hasMedia" class="flex-shrink-0 ml-2">
            <button
              @click="openPlayer(item)"
              class="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors duration-200"
              title="Open in main player"
            >
              <ArrowTopRightOnSquareIcon class="w-5 h-5" />
            </button>
          </div> -->
          <!-- Blocked/No Media Info text -->
          <div v-if="!item.hasMedia" class="text-xs text-gray-400 dark:text-gray-500 italic mr-1">
            No Media
          </div>
        </li>
      </ul>
    </div>
  </SidePanel>

  <!-- Fixed footer mini player bar (Option 1): teleported to body so no
       ancestor transform/filter can hijack its viewport-fixed position -->
  <Teleport to="body">
  <div
    v-if="miniPlayerVisible"
    ref="miniWrapRef"
    class="fixed z-[300] w-max max-w-[calc(100vw-1.5rem)]"
    :class="miniPos ? '' : 'left-3 md:left-[92px]'"
    :style="miniPos ? { left: miniPos.x + 'px', top: miniPos.y + 'px' } : { bottom: 'max(50px, calc(env(safe-area-inset-bottom) + 38px))' }"
  >
    <div
      class="relative flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur shadow-2xl pl-2 pr-2 py-2 select-none touch-none"
      :class="miniDragging ? 'cursor-grabbing' : 'cursor-grab'"
      @pointerdown="onMiniPointerDown"
      @pointermove="onMiniPointerMove"
      @pointerup="onMiniPointerUp"
      @pointercancel="onMiniPointerUp"
    >
      <div class="absolute left-4 right-4 top-0 h-6 -translate-y-1/2 flex items-center cursor-pointer touch-manipulation" @click="seekFromClick">
        <div class="w-full h-[5px] rounded-full bg-gray-200/70 dark:bg-gray-700/70 overflow-hidden pointer-events-none">
          <div class="h-full rounded-full bg-indigo-500 transition-[width] duration-300" :style="{ width: currentTimePercentage + '%' }"></div>
        </div>
      </div>
      <div v-if="miniYoutubeVisible" key="mini-video-box" class="yt-mini relative flex-shrink-0 overflow-hidden rounded-md bg-black" style="width:112px; aspect-ratio:16/9;">
        <SafeYoutube
          :key="'mini-' + (youtubeVideoId || '')"
          :ref="setMiniPlayerRef"
          :videoid="youtubeVideoId"
          :autoplay="isPlaying ? 1 : 0"
          :loop="0"
          :controls="0"
          :start-seconds="handoffStart"
          @ended="youtubeEnded"
          @paused="youtubePaused"
          @played="youtubePlayed"
        />
      </div>
      <div v-else key="mini-avatar-box" class="flex-shrink-0 flex items-center justify-center">
        <UserAvatar :user="{ name: miniTitle }" :size="36" />
      </div>
      <div class="flex flex-col min-w-0 w-[150px] sm:w-[190px] gap-0.5">
        <!-- Linha 1: título + restaurar + fechar -->
        <div class="flex items-center gap-0.5">
          <p class="flex-1 min-w-0 truncate text-xs font-semibold text-gray-900 dark:text-white leading-tight" :title="miniTitle + (miniArtist ? ' - ' + miniArtist : '')">{{ miniTitle }}</p>
          <Tooltip text="Restore playlist panel" position="top" zIndex="350">
            <button
              type="button"
              @click="open"
              class="flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-indigo-600 dark:text-gray-500 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ArrowsPointingOutIcon class="size-3.5" />
            </button>
          </Tooltip>
          <Tooltip text="Stop and close player" position="top" zIndex="350">
            <button
              type="button"
              @click="stopAndHide"
              class="flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon class="size-3.5" />
            </button>
          </Tooltip>
        </div>
        <!-- Linha 2: artista -->
        <p class="truncate text-[10px] text-gray-500 dark:text-gray-400 leading-tight">{{ miniArtist }}</p>
        <!-- Linha 3: tempo + controles -->
        <div class="flex items-center gap-1 mt-0.5">
          <span class="flex-shrink-0 w-9 text-[11px] font-medium tabular-nums text-gray-600 dark:text-gray-300">{{ formatTime }}</span>
          <div class="flex flex-1 items-center justify-center gap-1">
            <Tooltip text="Previous" position="top" zIndex="350">
              <button
                type="button"
                @click="playPrevManual"
                class="flex-shrink-0 p-1 rounded-full text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors touch-manipulation select-none active:scale-95"
              >
                <ChevronLeftIcon class="w-4 h-4 pointer-events-none" />
              </button>
            </Tooltip>
            <Tooltip :text="isPlaying ? 'Pause preview' : 'Play preview'" position="top" zIndex="350">
              <button
                type="button"
                @click="togglePlayFromMini"
                class="flex-shrink-0 flex items-center justify-center size-8 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md active:scale-95 touch-manipulation select-none"
              >
                <PauseIcon v-if="isPlaying" class="size-4 pointer-events-none" />
                <PlayIcon v-else class="size-4 translate-x-[0.5px] pointer-events-none" />
              </button>
            </Tooltip>
            <Tooltip text="Next" position="top" zIndex="350">
              <button
                type="button"
                @click="playNextManual"
                class="flex-shrink-0 p-1 rounded-full text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors touch-manipulation select-none active:scale-95"
              >
                <ChevronRightIcon class="w-4 h-4 pointer-events-none" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, defineExpose, reactive, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import playlistStore from '@/store/playlist.store';
import songService from '@/service/song.service';
import { NoSymbolIcon, PlayIcon, PauseIcon, ArrowsPointingOutIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { appEvents } from '@/store/app.events';
import SafeYoutube from '@/components/common/SafeYoutube.vue';
import UserAvatar from '@/components/common/UserAvatar.vue';
import Tooltip from '@/components/common/elements/TriadTooltip.vue';

const { t } = useI18n();

const route = useRoute();
const router = useRouter();

const sidePanel = ref(null);
const audioPlayer = ref(null);
const accordionPlayerRef = ref(null);
const miniPlayerRef = ref(null);
const youtubeVideoId = ref(null);

// NOTE: cada host (accordion da sidebar e mini player) tem seu próprio ref.
// Compartilhar um único ref entre dois v-if alternados quebra por ordem de
// patch (o null do unmount pode sobrescrever a instância recém-montada).
// O getter escolhe pelo host visível — determinístico e imune à ordem.
const setAccordionPlayerRef = (el) => {
  accordionPlayerRef.value = el || null;
};
const setMiniPlayerRef = (el) => {
  miniPlayerRef.value = el || null;
};
const activeYoutubePlayer = () => {
  const inst = panelVisible.value ? accordionPlayerRef.value : miniPlayerRef.value;
  return inst?.player || null;
};

// --- Mini player arrastável: posição persistida em localStorage ---
const MINI_POS_KEY = 'mytriad_mini_player_pos';
const miniWrapRef = ref(null);
const miniDragging = ref(false);
const loadMiniPos = () => {
  try {
    const v = JSON.parse(localStorage.getItem(MINI_POS_KEY));
    if (v && Number.isFinite(v.x) && Number.isFinite(v.y)) return v;
  } catch (e) { /* ignore */ }
  return null;
};
const miniPos = ref(loadMiniPos());
let dragOffset = null;
let dragMoved = false;

const clampMiniPos = (x, y) => {
  const el = miniWrapRef.value;
  const w = el?.offsetWidth || 300;
  const h = el?.offsetHeight || 60;
  return {
    x: Math.min(Math.max(0, x), Math.max(0, window.innerWidth - w)),
    y: Math.min(Math.max(0, y), Math.max(0, window.innerHeight - h)),
  };
};

const onMiniPointerDown = (e) => {
  if (e.button !== undefined && e.button !== 0) return;
  // Não inicia arraste em controles interativos nem na barra de progresso.
  if (e.target.closest('button, a, input, iframe, [data-no-drag]') || e.target.closest('.cursor-pointer')) return;
  const rect = miniWrapRef.value.getBoundingClientRect();
  dragOffset = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, sx: e.clientX, sy: e.clientY };
  dragMoved = false;
  e.currentTarget.setPointerCapture?.(e.pointerId);
};

const onMiniPointerMove = (e) => {
  if (!dragOffset) return;
  if (!dragMoved && Math.hypot(e.clientX - dragOffset.sx, e.clientY - dragOffset.sy) < 4) return;
  dragMoved = true;
  miniDragging.value = true;
  miniPos.value = clampMiniPos(e.clientX - dragOffset.dx, e.clientY - dragOffset.dy);
};

const onMiniPointerUp = (e) => {
  if (!dragOffset) return;
  e.currentTarget.releasePointerCapture?.(e.pointerId);
  dragOffset = null;
  miniDragging.value = false;
  if (dragMoved && miniPos.value) {
    localStorage.setItem(MINI_POS_KEY, JSON.stringify(miniPos.value));
  }
};

// Mantém o mini player visível se a janela encolher.
const onWindowResize = () => {
  if (miniPos.value) miniPos.value = clampMiniPos(miniPos.value.x, miniPos.value.y);
};

const playlist = computed(() => playlistStore.state.playlist);
const currentSongId = computed(() => route.params.songId);

const processedSongs = ref([]);
const playingSongId = ref(null);
const isPlaying = ref(false);
const currentTimeSeconds = ref(0);
const duration = ref(0);
const playInSequence = ref(localStorage.getItem('mytriad_play_in_sequence') === 'true');
const panelVisible = ref(false);

const miniPlayerVisible = computed(() => !!playingSongId.value && !panelVisible.value);
const miniYoutubeVisible = computed(() => !panelVisible.value && !!youtubeVideoId.value && !!playingSongId.value);

// Handoff timestamp when the visible player instance switches hosts
// (sidebar accordion <-> footer mini player). Moving an iframe across the
// DOM would reload it, so a fresh instance resumes from the saved time.
const pendingSeekRef = ref(null);
// Tempo de retomada passado direto ao novo player (já nasce na posição certa,
// sem tocar do 0 e sem seek posterior, evitando o "segundo repetido").
const handoffStart = ref(0);
let lastSampleAt = 0;

// Ao trocar o host visível do player, estima o tempo exato (última amostra +
// tempo decorrido desde ela) antes do player antigo ser destruído.
watch(panelVisible, () => {
  if (youtubeVideoId.value && playingSongId.value) {
    let t = currentTimeSeconds.value || 0;
    if (isPlaying.value && lastSampleAt) {
      t += (performance.now() - lastSampleAt) / 1000;
    }
    handoffStart.value = t;
  }
}, { flush: 'pre' });
watch(youtubeVideoId, () => { handoffStart.value = 0; });

const currentMiniItem = computed(() => {
  if (!playingSongId.value) return null;
  return processedSongs.value.find(s => s.id === playingSongId.value) || null;
});

const miniTitle = computed(() => currentMiniItem.value?.name || '');
const miniArtist = computed(() => currentMiniItem.value?.artist || '');

const hasYoutubeMedia = (item) => {
  return (item?.medias || []).some(m => m.type === 'youtube');
};

// Accordion open: this row is playing a YouTube video (frame expanded).
const isExpandedYt = (item) => {
  return playingSongId.value === item.id && !!youtubeVideoId.value;
};

watch(playInSequence, (newVal) => {
  localStorage.setItem('mytriad_play_in_sequence', newVal ? 'true' : 'false');
});

const currentTimePercentage = computed(() => {
  if (!duration.value) return 0;
  return (currentTimeSeconds.value / duration.value) * 100;
});

const formatTime = computed(() => {
  const minutes = Math.floor((currentTimeSeconds.value % 3600) / 60);
  const seconds = Math.floor(currentTimeSeconds.value % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

const formatDuration = computed(() => {
  const minutes = Math.floor((duration.value % 3600) / 60);
  const seconds = Math.floor(duration.value % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

const getVideoId = (urlValue) => {
  try {
    const urlObj = new URL(urlValue);
    if (urlObj.hostname.includes('youtu.be')) {
      return urlObj.pathname.substring(1);
    }
    if (urlObj.pathname.includes('/embed/')) {
      return urlObj.pathname.split('/embed/')[1];
    }
    return urlObj.searchParams.get('v');
  } catch (error) {
    console.error("Error parsing video id:", error);
    return null;
  }
};

let youtubeTimer = null;
let ytVerifyTimer = null;

const ytPlayer = () => {
  const p = activeYoutubePlayer();
  if (!p) {
    console.warn('[SidePanel] YouTube player not ready yet');
    return null;
  }
  return p;
};

// Reconcilia o estado com a realidade do player (cura dessincronias como
// autoplay bloqueado ou comandos emitidos antes do player existir).
const verifyYoutubeState = () => {
  try {
    const p = activeYoutubePlayer();
    if (!p || !youtubeVideoId.value || !playingSongId.value) return;
    const playingState = window.YT?.PlayerState?.PLAYING ?? 1;
    const playing = typeof p.getPlayerState === 'function' && p.getPlayerState() === playingState;
    isPlaying.value = playing;
    if (playing) startYoutubePolling();
    else stopYoutubePolling();
  } catch (e) {
    // ignore
  }
};

const scheduleYoutubeVerify = (ms = 3000) => {
  clearTimeout(ytVerifyTimer);
  ytVerifyTimer = setTimeout(verifyYoutubeState, ms);
};

const clearYoutubeVerify = () => {
  clearTimeout(ytVerifyTimer);
  ytVerifyTimer = null;
};

const startYoutubePolling = () => {
  stopYoutubePolling();
  youtubeTimer = setInterval(async () => {
    const p = activeYoutubePlayer();
    if (p) {
      try {
        const time = await p.getCurrentTime();
        if (Date.now() < seekGuardUntil) return;
        currentTimeSeconds.value = time;
        lastSampleAt = performance.now();
        const dur = await p.getDuration();
        duration.value = dur || 0;
      } catch (e) {
        // ignore errors if player is not fully ready
      }
    }
  }, 500);
};

const stopYoutubePolling = () => {
  if (youtubeTimer) {
    clearInterval(youtubeTimer);
    youtubeTimer = null;
  }
};

// Seek do slider: durante o arrasto só atualiza a UI e aplica o seek real
// depois de uma pausa (debounce), evitando dezenas de seekTo seguidos que
// causam saltos e segundos repetidos no áudio.
let seekDebounce = null;
let seekGuardUntil = 0;
const seekMedia = (e) => {
  const percentage = parseFloat(e.target.value);
  const targetTime = (percentage / 100) * duration.value;
  currentTimeSeconds.value = targetTime;
  seekGuardUntil = Date.now() + 1500;
  clearTimeout(seekDebounce);
  seekDebounce = setTimeout(() => seekTo(targetTime), 200);
};

const seekTo = (targetTime) => {
  // Ignora amostras do polling logo após o seek (valores antigos do player
  // fariam a barra voltar e disparariam novo seek).
  seekGuardUntil = Date.now() + 1500;
  if (youtubeVideoId.value) {
    const p = ytPlayer();
    if (p) {
      // Seek direto; NÃO guarda pendingSeek, senão o evento "played" que
      // segue o seek aplicaria um segundo seek (salto/repetição).
      try {
        p.seekTo(targetTime, true);
        currentTimeSeconds.value = targetTime;
      } catch (e) {
        // ignore
      }
    } else {
      // Player ainda não pronto: retoma assim que começar a tocar.
      pendingSeekRef.value = targetTime;
    }
  } else {
    if (audioPlayer.value) {
      audioPlayer.value.currentTime = targetTime;
      currentTimeSeconds.value = targetTime;
    }
  }
};

const skipTime = (seconds) => {
  let targetTime = currentTimeSeconds.value + seconds;
  if (targetTime < 0) targetTime = 0;
  if (targetTime > duration.value) targetTime = duration.value;
  seekTo(targetTime);
};

const youtubeEnded = () => {
  const endedId = playingSongId.value;
  isPlaying.value = false;
  playingSongId.value = null;
  youtubeVideoId.value = null;
  stopYoutubePolling();
  clearYoutubeVerify();
  pendingSeekRef.value = null;
  currentTimeSeconds.value = 0;
  duration.value = 0;
  if (endedId) {
    playNextSong(endedId);
  }
};

const youtubePaused = () => {
  isPlaying.value = false;
  stopYoutubePolling();
  clearYoutubeVerify();
};

const youtubePlayed = () => {
  isPlaying.value = true;
  appEvents.emit('pause-main-player');
  startYoutubePolling();
  clearYoutubeVerify();
  if (pendingSeekRef.value != null && pendingSeekRef.value > 1) {
    const t = pendingSeekRef.value;
    pendingSeekRef.value = null;
    try { activeYoutubePlayer()?.seekTo(t, true); } catch (e) { /* ignore */ }
  }
};

const isPlayingSong = (item) => {
  return playingSongId.value === item.id && isPlaying.value;
};

const open = () => {
  panelVisible.value = true;
  sidePanel.value?.open();
  loadAllMedias();
};

const close = () => {
  panelVisible.value = false;
  sidePanel.value?.close();
};

const onClose = () => {
  // Keep playback going: the footer mini player takes over (Option 1).
  panelVisible.value = false;
};

const onMinimize = () => {
  panelVisible.value = false;
  sidePanel.value?.close();
};

const togglePlayFromMini = () => {
  if (currentMiniItem.value) {
    togglePlay(currentMiniItem.value);
  }
};

const stopAndHide = () => {
  stopAudio();
};

const seekFromClick = (e) => {
  const el = e.currentTarget;
  const dur = Number(duration.value);
  if (!el || !Number.isFinite(dur) || dur <= 0) return;
  const rect = el.getBoundingClientRect();
  if (!rect || rect.width <= 0) return;
  const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
  seekTo(ratio * dur);
};

const getType = (preview) => {
  if (typeof preview !== 'string') return 'unknown';
  if (preview.indexOf("youtube") > -1 || preview.indexOf("youtu.be") > -1) {
    return "youtube";
  } else if (preview.indexOf("p.scdn.co") > -1) {
    return "spotify";
  } else {
    return "audio";
  }
};

const loadAllMedias = async () => {
  const currentPlaylist = playlist.value;
  if (!currentPlaylist || !currentPlaylist.songs) return;

  processedSongs.value = currentPlaylist.songs.map((song) => {
    const songId = song.data?._id || song.song_id || song.songID || song.song || song.id || song._id;
    const songData = song.data || {};
    
    const item = reactive({
      id: songId,
      name: songData.title || song.title || 'Untitled Song',
      artist: songData.artist?.name || song.artist?.name || song.userData?.artist?.name || '',
      originalSong: song,
      medias: [],
      loading: true,
      hasMedia: false
    });

    const spotifyEnabled = process.env.VUE_APP_SPOTIFY_SERVICE_ENABLED !== 'false';
    const youtubeEnabled = process.env.VUE_APP_YOUTUBE_SERVICE_ENABLED !== 'false';

    const filterMedia = (mediasList) => {
      // Show existing/pre-saved media regardless of service enablement flags
      return mediasList;
    };

    if (songData.media && songData.media.length > 0) {
      const parsed = songData.media.map((m, idx) => ({
        type: m.type || getType(m.preview || m),
        name: m.name || `Media ${idx + 1}`,
        preview: m.preview || m
      }));
      item.medias = filterMedia(parsed).filter(m => m.preview);
      item.hasMedia = item.medias.length > 0;
      item.loading = false;
    } else {
      if (!spotifyEnabled && !youtubeEnabled) {
        item.hasMedia = false;
        item.loading = false;
      } else {
        // Fetch media asynchronously
        songService.fetchMedia(songId).then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            const parsed = data.map((m, idx) => ({
              type: m.type || getType(m.preview),
              name: m.name || `${item.name} Media ${idx + 1}`,
              preview: m.preview
            }));
            item.medias = filterMedia(parsed).filter(m => m.preview);
            item.hasMedia = item.medias.length > 0;
          }
        }).catch((err) => {
          console.error('Failed to fetch media for song:', songId, err);
        }).finally(() => {
          item.loading = false;
        });
      }
    }

    return item;
  });
};

const togglePlay = (item) => {
  if (playingSongId.value === item.id) {
    if (isPlaying.value) {
      if (youtubeVideoId.value) {
        try { ytPlayer()?.pauseVideo(); } catch (e) { /* ignore */ }
      } else {
        audioPlayer.value?.pause();
      }
      isPlaying.value = false;
      clearYoutubeVerify();
    } else {
      if (youtubeVideoId.value) {
        try { ytPlayer()?.playVideo(); } catch (e) { /* ignore */ }
      } else {
        audioPlayer.value?.play();
      }
      isPlaying.value = true;
      appEvents.emit('pause-main-player');
      if (youtubeVideoId.value) {
        startYoutubePolling();
        scheduleYoutubeVerify();
      }
    }
  } else {
    // When switching songs, pause current playback directly without triggering
    // a transient unmount/null state in the Virtual DOM.
    if (audioPlayer.value) {
      audioPlayer.value.pause();
    }
    const p = activeYoutubePlayer();
    if (p) {
      try { p.stopVideo(); } catch (e) { /* ignore */ }
    }
    stopYoutubePolling();
    clearYoutubeVerify();
    pendingSeekRef.value = null;
    currentTimeSeconds.value = 0;
    duration.value = 0;

    const media = item.medias[0];
    if (media && media.preview) {
      playingSongId.value = item.id;
      if (media.type === 'youtube' || (media.preview && (media.preview.includes('youtube') || media.preview.includes('youtu.be')))) {
        youtubeVideoId.value = getVideoId(media.preview);
        isPlaying.value = true;
        appEvents.emit('pause-main-player');
        scheduleYoutubeVerify();
      } else {
        youtubeVideoId.value = null;
        if (audioPlayer.value) {
          audioPlayer.value.src = media.preview;
          audioPlayer.value.load();
          audioPlayer.value.play().then(() => {
            isPlaying.value = true;
            appEvents.emit('pause-main-player');
          }).catch(err => {
            console.error("Playback failed", err);
            isPlaying.value = false;
            playingSongId.value = null;
          });
        }
      }
    } else {
      playingSongId.value = null;
      youtubeVideoId.value = null;
      isPlaying.value = false;
    }
  }
};

const stopAudio = () => {
  if (audioPlayer.value) {
    audioPlayer.value.pause();
  }
  stopYoutube();
  stopYoutubePolling();
  clearYoutubeVerify();
  pendingSeekRef.value = null;
  isPlaying.value = false;
  playingSongId.value = null;
  currentTimeSeconds.value = 0;
  duration.value = 0;
};

const stopYoutube = () => {
  const p = activeYoutubePlayer();
  if (p) {
    try {
      p.stopVideo();
    } catch (e) {
      // ignore player stop errors if already stopped/unmounted
    }
  }
  youtubeVideoId.value = null;
};

const audioEnded = () => {
  const endedId = playingSongId.value;
  isPlaying.value = false;
  playingSongId.value = null;
  clearYoutubeVerify();
  if (endedId) {
    playNextSong(endedId);
  }
};

const playNextSong = (endedSongId, manual = false) => {
  // The sequence toggle gates AUTO-advance; manual prev/next always work.
  if (!playInSequence.value && !manual) return;

  const currentIndex = processedSongs.value.findIndex(s => s.id === endedSongId);
  if (currentIndex === -1) return;

  let nextIndex = currentIndex + 1;
  let nextSong = null;
  while (nextIndex < processedSongs.value.length) {
    const song = processedSongs.value[nextIndex];
    if (song.hasMedia) {
      nextSong = song;
      break;
    }
    nextIndex++;
  }

  if (nextSong) {
    togglePlay(nextSong);
    nextTick(() => {
      selectSong(nextSong.originalSong, true);
    });
  }
};

const playPrevSong = (manual = false) => {
  if (!playInSequence.value && !manual) return;
  if (!playingSongId.value) return;

  const currentIndex = processedSongs.value.findIndex(s => s.id === playingSongId.value);
  if (currentIndex === -1) return;

  let prevIndex = currentIndex - 1;
  let prevSong = null;
  while (prevIndex >= 0) {
    const song = processedSongs.value[prevIndex];
    if (song.hasMedia) {
      prevSong = song;
      break;
    }
    prevIndex--;
  }

  if (prevSong) {
    togglePlay(prevSong);
    nextTick(() => {
      selectSong(prevSong.originalSong, true);
    });
  }
};

const playNextManual = () => {
  if (playingSongId.value) playNextSong(playingSongId.value, true);
};

const playPrevManual = () => {
  playPrevSong(true);
};

const selectSong = (song, keepPanelOpen = false) => {
  const songId = song.data?._id || song.song_id || song.songID || song.song || song.id || song._id;
  router.push({ name: 'Playlist Songs', params: { id: route.params.id, songId } });
  if (window.innerWidth < 640 && !keepPanelOpen) {
    close();
  }
};

const onAudioTimeUpdate = () => {
  if (audioPlayer.value && !youtubeVideoId.value) {
    currentTimeSeconds.value = audioPlayer.value.currentTime;
    duration.value = audioPlayer.value.duration || 0;
  }
};

const onAudioLoadedMetadata = () => {
  if (audioPlayer.value && !youtubeVideoId.value) {
    duration.value = audioPlayer.value.duration || 0;
  }
};

watch(() => playlist.value, () => {
  loadAllMedias();
}, { deep: true });

// Navegação por swipe/seta/teclado: se o player está ativo (painel aberto ou
// mini player), acompanha a música da rota trocando a mídia também.
// Quando a troca já veio do próprio player (playNext/Prev), o id coincide
// com playingSongId e nada é feito.
let stopPendingMediaWatch = null;
watch(currentSongId, (newId) => {
  if (stopPendingMediaWatch) { stopPendingMediaWatch(); stopPendingMediaWatch = null; }
  if (!newId || !playingSongId.value || newId === playingSongId.value) return;
  const item = processedSongs.value.find(s => s.id === newId);
  if (!item) return;
  if (item.loading) {
    // Mídias ainda carregando: aguarda e então troca (se ainda ativo).
    stopPendingMediaWatch = watch(() => item.loading, (loading) => {
      if (loading) return;
      stopPendingMediaWatch?.();
      stopPendingMediaWatch = null;
      if (currentSongId.value === item.id && item.hasMedia && playingSongId.value !== item.id) togglePlay(item);
    });
    return;
  }
  // Sem mídia: mantém a reprodução atual em vez de interromper.
  if (item.hasMedia) togglePlay(item);
});

onMounted(() => {
  if (playlist.value) {
    loadAllMedias();
  }
  appEvents.on('pause-side-panel-player', stopAudio);
  window.addEventListener('resize', onWindowResize);
  nextTick(onWindowResize);
});

onUnmounted(() => {
  appEvents.off('pause-side-panel-player', stopAudio);
  window.removeEventListener('resize', onWindowResize);
  stopYoutubePolling();
  clearYoutubeVerify();
});

defineExpose({ open, close });
</script>

<style scoped>
/* YouTube frame fills its 16:9 accordion box (responsive, >= 200px tall) */
.yt-frame :deep(iframe),
.yt-mini :deep(iframe) {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  border: 0;
}
</style>
